import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, Send, UserPlus, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow } from "@/components/admin/EditorialList";
import EditorialDialog, { EditorialField } from "@/components/admin/EditorialDialog";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";
import { FrameIllustration } from "@/components/admin/EditorialIllustrations";

export const INQUIRY_STATUSES = [
  { id: "new", label: "New", dot: "bg-rose-500" },
  { id: "contacted", label: "Contacted", dot: "bg-amber-400" },
  { id: "booked", label: "Booked", dot: "bg-emerald-500" },
  { id: "lost", label: "Lost", dot: "bg-stone-500" },
];

const statusOf = (q) => q.status || "new";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [params, setParams] = useSearchParams();
  const openId = params.get("open");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/inquiries");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of INQUIRY_STATUSES) c[s.id] = items.filter((q) => statusOf(q) === s.id).length;
    return c;
  }, [items]);

  const visible = filter === "all" ? items : items.filter((q) => statusOf(q) === filter);
  const selected = openId ? items.find((q) => q.id === openId) : null;

  const patchLocal = (updated) => setItems((list) => list.map((q) => (q.id === updated.id ? updated : q)));
  const open = (id) => setParams(id ? { open: id } : {});

  return (
    <div className="space-y-8" data-testid="admin-inquiries-page">
      <AdminPageHeader
        breadcrumb={["CLIENTS", "INQUIRIES"]}
        title="Inquiries"
        count={counts.new}
      />

      <div className="flex flex-wrap gap-2" data-testid="inquiry-filters">
        {[{ id: "all", label: "All" }, ...INQUIRY_STATUSES].map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className={`inline-flex items-center gap-2 px-3 h-8 text-[10px] uppercase tracking-[0.24em] border transition-colors ${
              filter === s.id ? "border-foreground text-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`inquiry-filter-${s.id}`}
          >
            {s.dot && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
            {s.label}
            <span className="tabular-nums opacity-60">{counts[s.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {!loading && items.length === 0 ? (
        <EditorialEmptyState
          icon={<FrameIllustration />}
          eyebrow="The inbox is quiet"
          headline={<>No inquiries <em>yet</em>.</>}
          description="When someone writes to you through your website's contact form, it lands here — reply, track the conversation, and turn it into a booking."
          tone="charcoal"
          backdrop={EDITORIAL_ASSETS.editorial_bw_portrait.src}
          data-testid="inquiries-empty"
        />
      ) : (
        <EditorialList>
          {loading && (
            <div className="py-10 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Loading…</div>
          )}
          {visible.map((q, i) => {
            const st = INQUIRY_STATUSES.find((s) => s.id === statusOf(q)) || INQUIRY_STATUSES[0];
            const unread = statusOf(q) === "new" && !q.read_at;
            return (
              <EditorialRow
                key={q.id}
                index={i}
                onClick={() => open(q.id)}
                title={<span className={unread ? "font-medium" : ""}>{q.name || q.email}</span>}
                meta={[
                  q.email,
                  fmtDate(q.created_at),
                  (q.replies || []).length ? `${q.replies.length} repl${q.replies.length === 1 ? "y" : "ies"}` : null,
                ]}
                status={
                  <span className="inline-flex items-center gap-3">
                    {q.awaiting_studio && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-sky-500" data-testid="inquiry-client-replied">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" /> Replied
                      </span>
                    )}
                    <StatusPill status={st} />
                  </span>
                }
                data-testid={`inquiry-row-${q.id}`}
              >
                <span className="basis-full text-sm text-muted-foreground line-clamp-1">{q.message}</span>
              </EditorialRow>
            );
          })}
          {!loading && visible.length === 0 && (
            <div className="py-10 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground">Nothing in this stage</div>
          )}
        </EditorialList>
      )}

      {selected && (
        <InquiryDialog inquiry={selected} onClose={() => open(null)} onChange={patchLocal} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground shrink-0" data-testid={`inquiry-status-${status.id}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
      {status.label}
    </span>
  );
}

function InquiryDialog({ inquiry, onClose, onChange }) {
  const [subject, setSubject] = useState(`Re: your inquiry — ${inquiry.name?.split(" ")[0] || "hello"}`);
  const [body, setBody] = useState("");
  const [bookingLink, setBookingLink] = useState(false);
  const [notes, setNotes] = useState(inquiry.notes || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!inquiry.read_at) api.post(`/admin/inquiries/${inquiry.id}/read`).catch(() => {});
  }, [inquiry.id, inquiry.read_at]);

  async function setStatus(status) {
    try {
      const r = await api.patch(`/admin/inquiries/${inquiry.id}`, { status });
      onChange(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  }
  async function saveNotes() {
    if ((inquiry.notes || "") === notes) return;
    try {
      const r = await api.patch(`/admin/inquiries/${inquiry.id}`, { notes });
      onChange(r.data);
      toast.success("Notes saved");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  }
  async function sendReply() {
    if (!body.trim()) return toast.error("Write a reply first");
    setSending(true);
    try {
      const r = await api.post(`/admin/inquiries/${inquiry.id}/reply`, { subject, body, include_booking_link: bookingLink });
      onChange(r.data);
      setBody("");
      toast.success(`Reply sent to ${inquiry.email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setSending(false); }
  }
  async function convert() {
    try {
      const r = await api.post(`/admin/inquiries/${inquiry.id}/convert`);
      onChange(r.data);
      toast.success(`${inquiry.name || inquiry.email} added to your clients`);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  }

  const current = statusOf(inquiry);
  return (
    <EditorialDialog
      open
      onClose={onClose}
      breadcrumb="INQUIRY"
      title={inquiry.name || inquiry.email}
      subtitle={fmtDate(inquiry.created_at)}
      size="lg"
      data-testid="inquiry-dialog"
    >
      <div className="px-6 sm:px-8 py-6 space-y-8">
        {/* Contact + pipeline */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <a href={`mailto:${inquiry.email}`} className="inline-flex items-center gap-2 hover:underline" data-testid="inquiry-email-link">
            <Mail className="w-3.5 h-3.5 opacity-60" /> {inquiry.email}
          </a>
          {inquiry.phone && (
            <a href={`tel:${inquiry.phone}`} className="inline-flex items-center gap-2 hover:underline" data-testid="inquiry-phone-link">
              <Phone className="w-3.5 h-3.5 opacity-60" /> {inquiry.phone}
            </a>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="inquiry-pipeline">
          {INQUIRY_STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className={`inline-flex items-center gap-2 px-3 h-8 text-[10px] uppercase tracking-[0.24em] border transition-colors ${
                current === s.id ? "border-[color:var(--gold-foil)] text-[color:var(--gold-foil)]" : "border-white/15 text-[color:var(--cream)]/60 hover:text-[color:var(--cream)]"
              }`}
              data-testid={`inquiry-set-status-${s.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.label}
            </button>
          ))}
          <div className="ml-auto">
            {inquiry.customer_id ? (
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-emerald-400" data-testid="inquiry-converted">
                <Check className="w-3.5 h-3.5" /> In clients
              </span>
            ) : (
              <button
                onClick={convert}
                className="inline-flex items-center gap-2 px-3 h-8 text-[10px] uppercase tracking-[0.24em] border border-[color:var(--gold-foil)]/60 text-[color:var(--gold-foil)] hover:bg-[color:var(--gold-foil)]/10 transition-colors"
                data-testid="inquiry-convert"
              >
                <UserPlus className="w-3.5 h-3.5" /> Add to clients
              </button>
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="space-y-3" data-testid="inquiry-thread">
          <Bubble who={inquiry.name || "Client"} when={inquiry.created_at} text={inquiry.message} />
          {(inquiry.replies || []).map((r) =>
            r.direction === "client" ? (
              <Bubble key={r.id} who={inquiry.name || "Client"} when={r.sent_at} text={r.body} client />
            ) : (
              <Bubble key={r.id} who="You" when={r.sent_at} text={r.body} subject={r.subject} mine />
            ),
          )}
        </div>

        {/* Composer */}
        <div className="space-y-4 border-t border-white/10 pt-6" data-testid="inquiry-composer">
          <EditorialField label="Subject">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input w-full" data-testid="inquiry-reply-subject" />
          </EditorialField>
          <EditorialField label="Reply" hint="Sent from your studio's email identity; the client's original message is quoted underneath.">
            <textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="input w-full min-h-[120px]"
              placeholder={`Hi ${inquiry.name?.split(" ")[0] || "there"}, thank you for reaching out…`}
              data-testid="inquiry-reply-body"
            />
          </EditorialField>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-[color:var(--cream)]/70 cursor-pointer">
              <input type="checkbox" checked={bookingLink} onChange={(e) => setBookingLink(e.target.checked)} data-testid="inquiry-reply-booking-link" />
              Include a "Book your session" button
            </label>
            <button
              onClick={sendReply}
              disabled={sending}
              className="ml-auto inline-flex items-center gap-2 px-5 h-10 bg-[color:var(--cream)] text-[#0E0D0C] text-[11px] uppercase tracking-[0.24em] disabled:opacity-50 hover:opacity-90 transition-opacity"
              data-testid="inquiry-reply-send"
            >
              <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>

        {/* Notes */}
        <EditorialField label="Private notes" hint="Only your studio sees this.">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={saveNotes}
            className="input w-full"
            placeholder="Budget, dates discussed, follow-up plan…"
            data-testid="inquiry-notes"
          />
        </EditorialField>
      </div>
    </EditorialDialog>
  );
}

function Bubble({ who, when, text, subject, mine, client }) {
  return (
    <div className={`max-w-[92%] ${mine ? "ml-auto" : ""}`} data-testid={client ? "inquiry-client-reply" : undefined}>
      <div className={`px-4 py-3 text-sm whitespace-pre-wrap ${mine ? "bg-[color:var(--gold-foil)]/10 border border-[color:var(--gold-foil)]/30" : "bg-white/[0.04] border border-white/10"}`}>
        {subject && <div className="text-[10px] uppercase tracking-[0.2em] opacity-60 mb-1">{subject}</div>}
        {text}
      </div>
      <div className={`mt-1 text-[10px] uppercase tracking-[0.2em] opacity-50 ${mine ? "text-right" : ""}`}>
        {who} · {fmtDate(when)}
      </div>
    </div>
  );
}
